import { ClubTierTypes } from './clubs';
import { SubscriptionTypes, ThirdPartyProviders, ThirdPartyProviderIds } from '../components/Analytics/constants';
import { BillingCountryIds, ClubPowerTermStatus, SubscriptionIntervalTypes } from './constants';

export const userIsClubOwner = (clubMembership) => {
    return clubMembership?.roleValue?.toLowerCase() === 'owner';
};

export const userIsClubAdminOrOwner = (clubMembership) => {
    const normalizedRole = clubMembership?.roleValue?.toLowerCase();
    return normalizedRole === 'owner' || normalizedRole === 'admin';
};

export const userIsFreeCollegeClubOwner = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership?.tierTypeName?.toLowerCase() === 'free college' && userIsClubOwner(clubMembership));
    });
};

export const userIsProCollegeClubOwner = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership?.tierTypeName?.toLowerCase() === 'pro college' && userIsClubOwner(clubMembership));
    });
};

export const userIsProCollegeAdminOrClubOwner = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership?.tierTypeName?.toLowerCase() === 'pro college' && userIsClubAdminOrOwner(clubMembership));
    });
};

export const userIsFreeOrProCollegeClubOwner = (user) => {
    return (userIsFreeCollegeClubOwner(user) || userIsProCollegeClubOwner(user));
};

export const userIsFreeHighSchoolClubOwner = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership?.tierTypeName?.toLowerCase() === 'high school' && userIsClubOwner(clubMembership));
    });
};

export const userIsFreeCollegeAdminOrClubOwner = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership?.tierTypeName?.toLowerCase() === 'free college' && userIsClubAdminOrOwner(clubMembership));
    });
};

export const userIsFreeOrProCollegeClubOwnerOrAdmin = (user) => {
    return (userIsFreeCollegeAdminOrClubOwner(user) || userIsProCollegeAdminOrClubOwner(user));
};

export const userIsPaidHitter = (user) => {
    return user?.clubMemberships?.find((clubMembership) => {
        return (clubMembership.tierTypeId === ClubTierTypes.PAID_HIT);
    });
};

export const userIsNotPowered = (props) => {
    return props?.user && !props.user.isPowered;
};

/**
 * Determines whether a user can access Chat with Ace.
 * Requires the primary gate and an authenticated user.
 */
export const canAccessChatWithAce = ({ user, isChatWithAceEnabled }) => {
    return !!isChatWithAceEnabled && !!user && !!user.claimedPlayer?.playerId;
};

export const userHasGlobalPrivileges = (user) => {
    if (!user?.memberThirdPartyPlans?.length) {
        return false;
    }
    return user.memberThirdPartyPlans.some(
        (plan) => plan?.provider?.toLowerCase() === ThirdPartyProviders.GLOBAL_PRIVILEGES.toLowerCase(),
    );
};

export const isAnnualPowerSubscriber = (user) => Boolean(user?.isPowered) &&
    user?.subscriptionTierType === SubscriptionIntervalTypes.YEAR;

export const isMonthlyPowerSubscriber = (user) => Boolean(user?.isPowered) &&
    user?.subscriptionTierType === SubscriptionIntervalTypes.MONTH;

// The viewer's Power tier as one of SubscriptionTypes' free / monthly / annual values, for
// analytics. Distinct from deriveUserSubscriptionStatus, which buckets for the upgrade funnel
// (monthly / power_past_due / free).
export const getUserSubscriptionTier = (user) => {
    if (isAnnualPowerSubscriber(user)) {
        return SubscriptionTypes.ANNUAL;
    }
    if (isMonthlyPowerSubscriber(user)) {
        return SubscriptionTypes.MONTHLY;
    }
    return SubscriptionTypes.FREE;
};

const clubMembershipGrantsGlobalPrivileges = (cm) => {
    return Boolean(cm?.isPower) && cm?.powerTermStatus === ClubPowerTermStatus.ACTIVE;
};

// True when the member holds a club power assignment (isPower) at all, regardless
// of the power term. Use this only for "does this member have any club power
// grant"; to gate powered UI use userHasActiveClubPower below.
export const userIsClubPowered = (user) => {
    return user?.clubMemberships?.some((cm) => Boolean(cm?.isPower)) ?? false;
};

// True when the member is club-powered AND the club's power term is currently
// active. This is the gate for "treat like a powered member" UI — Fabletics / GP
// banners and suppressing Resume/Renew/Upgrade CTAs — and matches the exact rule
// Global Privileges access uses (clubMembershipGrantsGlobalPrivileges).
export const userHasActiveClubPower = (user) => {
    return user?.clubMemberships?.some(clubMembershipGrantsGlobalPrivileges) ?? false;
};

export const userCanAccessGlobalPrivileges = (user) => {
    return isAnnualPowerSubscriber(user) || userHasActiveClubPower(user) || userHasGlobalPrivileges(user);
};

// Single gate for the Fabletics module. Per backend (ENG-2163 comment #117093),
// subscriptionBillingCountryId is a server-resolved signal: active subscription's
// BIN country → profile residence country → null. A USA value (216) means the
// member should see Fabletics in some form (promo for Free/Monthly, CTA for
// entitled Annual); anything else means hide.
export const isUserFableticsEligible = (user) => user?.subscriptionBillingCountryId === BillingCountryIds.USA;

// Returns the member's active Fabletics plan when present. Only entitled
// Annual members will have an entry (backend omits inactive/revoked plans).
// Callers use the returned plan's ctaUrl to open the Fabletics activation page.
export const getFableticsEntitlement = (user) => {
    if (!user?.memberThirdPartyPlans?.length) {
        return null;
    }
    return user.memberThirdPartyPlans.find(
        (plan) => plan?.providerId === ThirdPartyProviderIds.FABLETICS && plan?.ctaUrl,
    ) || null;
};
